import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { goToApp } from '@/lib/track';

const navLinks = [
  { label: 'Sobre', href: '#sobre' },
  { label: 'Funcionalidades', href: '#funcionalidades' },
  { label: 'Depoimentos', href: '#depoimentos' },
  { label: 'Planos', href: '#planos' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPricing = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector('#planos');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass shadow-lg shadow-[#B54182]/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        <a href="#" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <img
            src="/logo-menoease-dark.svg"
            alt="MenoEase"
            className="h-9 md:h-10 w-auto"
          />
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="text-sm font-medium text-stone-600 hover:text-[#B54182] transition-colors relative group"
              data-testid={`nav-link-${link.href.slice(1)}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#B54182] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <button
            onClick={() => goToApp('navbar')}
            className="text-white shadow-lg hover:shadow-[#B54182]/25 transition-all duration-300 transform hover:-translate-y-0.5 rounded-full px-6 py-2.5 text-sm font-semibold"
            style={{ background: 'linear-gradient(135deg, #B54182, #BE5791)' }}
            data-testid="nav-cta-button"
          >
            Comece Grátis
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 text-stone-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-base font-medium text-stone-700 hover:text-[#B54182] py-2"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => goToApp('navbar-mobile')}
                className="text-white rounded-full px-6 py-3 text-sm font-semibold mt-2"
                style={{ background: 'linear-gradient(135deg, #B54182, #BE5791)' }}
                data-testid="mobile-cta-button"
              >
                Comece Grátis
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
